import { Check, Dices, MoonStar, Sun, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  type Adjustments,
  adjustPalette,
  brightsFor,
  flipPalette,
  HARMONIES,
  type Harmony,
  keepLocked,
  MOODS,
  type Mood,
  NO_ADJUSTMENTS,
  neutralRamp,
  type Palette,
  paletteFromSeed,
} from "@/color/generate";
import { fromLch, type Hex, toLch } from "@/color/oklch";
import { PALETTE_KEYS } from "@/model/palette";
import { cloneTheme, type Theme } from "@/model/theme";
import { useEditor } from "@/store/editor";
import { useUi } from "@/store/ui";
import { Button } from "@/ui/kit/button";
import { Segmented } from "@/ui/kit/segmented";
import { OklchPicker } from "../controls/oklch-picker";
import { GradientSlider } from "../controls/slider";

function Strip({ palette }: { palette: Palette }) {
  return (
    <div className="flex h-8 overflow-hidden rounded-md ring-1 ring-black/40">
      {PALETTE_KEYS.map((k) => (
        <span
          key={k}
          className="flex-1"
          style={{ background: palette[k] }}
          title={`${k} ${palette[k]}`}
        />
      ))}
    </div>
  );
}

function Panel({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="flex items-center border-line border-b px-4 py-3">
        <h2 className="flex-1 font-semibold text-sm">{title}</h2>
        <Button size="icon-sm" variant="ghost" onClick={onClose} aria-label="Close">
          <X />
        </Button>
      </div>
      <div className="space-y-4 p-4">{children}</div>
    </div>
  );
}

export function GenerateTool() {
  const draft = useEditor((s) => s.draft);
  const edit = useEditor((s) => s.edit);
  const open = useEditor((s) => s.open);
  const close = () => useUi.getState().openTool(null);
  const [seed, setSeed] = useState<Hex>(draft.palette.blue);
  const [harmony, setHarmony] = useState<Harmony>("natural");
  const [mood, setMood] = useState<Mood>("balanced");
  const [tint, setTint] = useState(0.5);
  const preview = useMemo(
    () =>
      keepLocked(
        draft.palette,
        paletteFromSeed(seed, { variant: draft.variant, harmony, mood, neutralTint: tint }),
        draft.locks,
      ),
    [seed, harmony, mood, tint, draft.variant, draft.palette, draft.locks],
  );
  const neutralsOnly = () => {
    const s = toLch(seed);
    const ramp = neutralRamp({
      variant: draft.variant,
      hue: s.h,
      chroma: tint * 0.03,
      bgL: toLch(draft.palette.bg1).l,
      fgL: toLch(draft.palette.fg1).l,
    });
    edit((t) => ({ ...t, palette: keepLocked(t.palette, { ...t.palette, ...ramp }, t.locks) }));
  };
  const flip = () => {
    const variant = draft.variant === "dark" ? "light" : "dark";
    const next: Theme = {
      ...cloneTheme(
        draft,
        `${draft.name.replace(/ (Dark|Light)$/i, "")} ${variant === "light" ? "Light" : "Dark"}`,
      ),
      variant,
      palette: flipPalette(draft.palette, draft.variant),
      // Literal hex overrides were picked for the other background; refs still make sense.
      overrides: Object.fromEntries(
        Object.entries(draft.overrides).filter(([, r]) => !r?.startsWith("#")),
      ),
      meta: { ...draft.meta, basedOn: draft.name },
    };
    open(next, null);
  };
  return (
    <Panel title="Generate" onClose={close}>
      <div className="space-y-2">
        <div className="font-medium text-xs">Seed color</div>
        <OklchPicker value={seed} onChange={setSeed} />
      </div>
      <div className="space-y-1.5">
        <div className="font-medium text-xs">Harmony</div>
        <Segmented
          value={harmony}
          onChange={setHarmony}
          options={HARMONIES.map((h) => ({ value: h.id, label: h.label, title: h.hint }))}
        />
        <p className="text-[11px] text-faint">{HARMONIES.find((h) => h.id === harmony)?.hint}</p>
      </div>
      <div className="space-y-1.5">
        <div className="font-medium text-xs">Mood</div>
        <Segmented
          value={mood}
          onChange={setMood}
          options={MOODS.map((m) => ({ value: m.id, label: m.label }))}
        />
      </div>
      <GradientSlider
        label="Tint"
        value={tint}
        min={0}
        max={1}
        step={0.05}
        onChange={setTint}
        format={(n) => `${Math.round(n * 100)}%`}
        gradient={`linear-gradient(to right, ${fromLch({ l: 0.3, c: 0, h: 0 })}, ${fromLch({ ...toLch(seed), l: 0.3, c: 0.05 })})`}
      />
      <Strip palette={preview} />
      <p className="text-[11px] text-faint">
        Locked swatches are kept. The result replaces the palette (undo with Ctrl+Z).
      </p>
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => edit((t) => ({ ...t, palette: preview }))}>
          <Check /> Apply palette
        </Button>
        <Button variant="outline" onClick={neutralsOnly}>
          Neutrals only
        </Button>
        <Button
          variant="ghost"
          onClick={() =>
            setSeed(
              fromLch({
                l: draft.variant === "dark" ? 0.72 : 0.55,
                c: 0.08 + Math.random() * 0.1,
                h: Math.random() * 360,
              }),
            )
          }
        >
          <Dices /> Random seed
        </Button>
      </div>
      <div className="space-y-2 border-line border-t pt-4">
        <div className="font-medium text-xs">Variants</div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              edit((t) => ({
                ...t,
                palette: keepLocked(
                  t.palette,
                  { ...t.palette, ...brightsFor(t.palette, t.variant) } as Palette,
                  t.locks,
                ),
              }))
            }
          >
            Derive bright ANSI from accents
          </Button>
          <Button variant="outline" size="sm" onClick={flip}>
            {draft.variant === "dark" ? <Sun /> : <MoonStar />} Derive{" "}
            {draft.variant === "dark" ? "light" : "dark"} version
          </Button>
        </div>
        <p className="text-[11px] text-faint">
          The derived version opens as a new unsaved preset; this one stays untouched.
        </p>
      </div>
    </Panel>
  );
}

export function AdjustTool() {
  const edit = useEditor((s) => s.edit);
  const draft = useEditor((s) => s.draft);
  const close = () => useUi.getState().openTool(null);
  // Sliders are absolute offsets from the palette as it was when the panel opened.
  const base = useRef(draft.palette);
  const [adj, setAdj] = useState<Adjustments>(NO_ADJUSTMENTS);
  useEffect(() => {
    base.current = useEditor.getState().draft.palette;
  }, []);
  const apply = (next: Adjustments) => {
    setAdj(next);
    edit((t) => ({ ...t, palette: adjustPalette(base.current, next, t.locks) }), "adjust");
  };
  const row = (
    key: keyof Adjustments,
    label: string,
    min: number,
    max: number,
    step: number,
    fmt: (n: number) => string,
  ) => (
    <GradientSlider
      label={label}
      value={adj[key]}
      min={min}
      max={max}
      step={step}
      onChange={(n) => apply({ ...adj, [key]: n })}
      format={fmt}
      className="grid-cols-[4.5rem_1fr_3.2rem]"
    />
  );
  return (
    <Panel title="Adjust" onClose={close}>
      <p className="text-muted text-xs">
        Changes apply live to unlocked swatches, as one undo step.
      </p>
      {row("hueShift", "Hue", -180, 180, 1, (n) => `${n > 0 ? "+" : ""}${n}°`)}
      {row("chroma", "Saturation", 0, 2, 0.01, (n) => `${Math.round(n * 100)}%`)}
      {row("contrast", "Contrast", 0.5, 1.5, 0.01, (n) => `${Math.round(n * 100)}%`)}
      {row(
        "brightness",
        "Background",
        -0.15,
        0.15,
        0.005,
        (n) => `${n > 0 ? "+" : ""}${Math.round(n * 100)}`,
      )}
      {row("warmth", "Warmth", -1, 1, 0.02, (n) =>
        n === 0 ? "0" : n > 0 ? `warm ${Math.round(n * 100)}` : `cool ${Math.round(-n * 100)}`,
      )}
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setAdj(NO_ADJUSTMENTS);
            edit((t) => ({ ...t, palette: base.current }), "adjust");
          }}
        >
          Reset
        </Button>
        <Button variant="primary" onClick={close}>
          <Check /> Done
        </Button>
      </div>
    </Panel>
  );
}
